import React, { FC, useContext, useMemo, useState, useEffect } from 'react'
import GenericPieChart from 'components/common/GenericPieChart'
import ChartLegend from 'components/common/ChartLegend'
import GenericHeading from 'components/common/GenericHeading'
import GenericBarsChart from 'components/common/GenericBarsChart'
import VerticalOverview from 'containers/VerticalPage/common/VerticalOverview/VerticalOverview'
import ChartWrapper from 'containers/VerticalPage/common/VerticalOverview/ChartWrapper'
import FinancialOverview from 'containers/VerticalPage/common/VerticalOverview/FinancialOverview'
import { VerticalContext } from 'store/verticals/Context'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import {
  VerticalBarChartLoader,
  VerticalPieChartLoader,
  VerticalTabsHeadingLoader,
} from 'containers/VerticalPage/common/Loaders'
import DatePicker from 'components/common/DatePicker'
import { getLabelsForFinancialOverview } from './utils'
import { getDateByYearsLater, getDefaultDateValues } from 'containers/VerticalPage/common/utils'
import { usePeriodSetter, useChartDataFetching } from 'containers/VerticalPage/common/hooks'
import { NOT_AVAILABLE } from 'utils/constants'
import { getVerticalRevenueByTotalFromEntity, getTotalRevenueFromEntity } from './utils'
import { getVerticalRevenueBySourcesFromEntity } from '../../nft/Overview/utils'
import moment from 'moment'
import { useCurrency } from 'contexts'
import { useCurrencyConversion } from 'hooks/useCurrencyConversion'

const calendarIconUrl = require('components/common/images/calendar.svg')
const periodPopperPlacement = 'bottom-end'

const periodFormat = 'MMM. yyyy'
const labelsForFinancialOverview = getLabelsForFinancialOverview()

const Overview: FC = () => {
  const { verticalEntity } = useContext(VerticalContext)
  const { convertCurrency, currentCurrency } = useCurrency()
  const { formatAmount } = useCurrencyConversion()
  const [totalRevenueGraphData, setTotalRevenueGraphData] = useState<any[]>([])
  const [totalRevenueDataForFinancialOverview, setTotalRevenueDataForFinancialOverview] = useState<any[]>([])
  const [totalGraphData, setTotalGraphData] = useState<any[]>([])
  const [sourceGraphData, setSourceGraphData] = useState<any[]>([])

  const defaultDate = {
    start: moment(getDateByYearsLater(-10)).format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD'),
  }

  const { period: piesPeriod, handlePeriodChange: handlePiesPeriodChange } = usePeriodSetter(defaultDate)
  const { period: barPeriod, handlePeriodChange: handleBarsPeriodChange } = usePeriodSetter(defaultDate)

  const { isLoadingPies, isLoadingBar } = useChartDataFetching()

  const theme = useTheme()

  useEffect(() => {
    const fetchSourcesPieChartData = async () => {
      if (!verticalEntity || !convertCurrency) return

      try {
        const sourcesData = await getVerticalRevenueBySourcesFromEntity(
          verticalEntity,
          piesPeriod,
          convertCurrency,
          currentCurrency,
        )
        setSourceGraphData(sourcesData)
      } catch (error) {
        console.error('Error fetching sources pie chart data:', error)
      }
    }

    fetchSourcesPieChartData()
  }, [verticalEntity, piesPeriod, convertCurrency, currentCurrency])

  useEffect(() => {
    const fetchPieChartData = async () => {
      if (!verticalEntity || !convertCurrency) return

      try {
        const pieData = await getVerticalRevenueByTotalFromEntity(
          verticalEntity,
          piesPeriod,
          convertCurrency,
          currentCurrency,
        )
        setTotalGraphData(pieData)
      } catch (error) {
        console.error('Error fetching pie chart data:', error)
      }
    }

    fetchPieChartData()
  }, [verticalEntity, piesPeriod, convertCurrency, currentCurrency])

  useEffect(() => {
    const fetchChartData = async () => {
      if (!verticalEntity || !convertCurrency) return

      try {
        const chartData = await getTotalRevenueFromEntity(
          verticalEntity,
          barPeriod,
          false,
          convertCurrency,
          currentCurrency,
        )
        setTotalRevenueGraphData(chartData)
      } catch (error) {
        console.error('Error fetching chart data:', error)
      }
    }

    fetchChartData()
  }, [verticalEntity, barPeriod, convertCurrency, currentCurrency])

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!verticalEntity || !convertCurrency) return

      try {
        const financialData = await getTotalRevenueFromEntity(
          verticalEntity,
          barPeriod,
          true,
          convertCurrency,
          currentCurrency,
        )
        setTotalRevenueDataForFinancialOverview(financialData)
      } catch (error) {
        console.error('Error fetching financial data:', error)
      }
    }

    fetchFinancialData()
  }, [verticalEntity, barPeriod, convertCurrency, currentCurrency])

  return (
    <VerticalOverview>
      <ChartWrapper>
        <Stack
          sx={{
            mb: { md: '6px', lg: '8px', xl: '12px', xxl: '18px', xxxl: '28px' },
            width: '100%',
          }}
        >
          {isLoadingPies ? (
            <VerticalTabsHeadingLoader />
          ) : (
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
              <GenericHeading
                sx={{
                  fontWeight: '600',
                  fontSize: { md: '14px', lg: '15px', xl: '18px', xxl: '28px', xxxl: '36px' },
                  color: '#1C2842',
                }}
              >
                REVENUE SOURCE: ALL
              </GenericHeading>
              <DatePicker
                periodStart={piesPeriod.start}
                periodEnd={piesPeriod.end}
                handlePeriodChange={handlePiesPeriodChange}
                format={periodFormat}
                popperPlacement={periodPopperPlacement}
                isCharts
                iconUrl={calendarIconUrl}
              />
            </Stack>
          )}
        </Stack>
        {isLoadingPies ? (
          <VerticalPieChartLoader />
        ) : (
          <Stack
            direction='row'
            width='100%'
            justifyContent='space-around'
            alignItems='center'
            sx={{
              flex: 1,
            }}
          >
            <Stack flexDirection='row-reverse' alignItems='center'>
              {sourceGraphData ? (
                <Stack
                  alignItems='center'
                  sx={{
                    width: { md: '75px', lg: '125px', lgPlus: '165px', xl: '165px', xxl: '250px', xxxl: '350px' },
                    ml: { md: '8px', lg: '16px', xl: '24px', xxl: '34px', xxxl: '40px' },
                  }}
                >
                  <GenericHeading
                    textAlign='center'
                    sx={{
                      fontSize: { md: '13px', lg: '14px', xl: '16px', xxl: '26px', xxxl: '38px' },
                      color: '#103072',
                      fontWeight: 600,
                    }}
                  >
                    Sources
                  </GenericHeading>
                  <GenericPieChart chartData={sourceGraphData} />
                </Stack>
              ) : (
                NOT_AVAILABLE
              )}
              {sourceGraphData ? <ChartLegend legendNames={sourceGraphData.map(datum => datum.name)} /> : NOT_AVAILABLE}
            </Stack>
            <Stack flexDirection='row-reverse' alignItems='center'>
              {totalGraphData ? (
                <Stack
                  alignItems='center'
                  sx={{
                    width: { md: '75px', lg: '125px', lgPlus: '165px', xl: '165px', xxl: '250px', xxxl: '350px' },
                    ml: { md: '8px', lg: '16px', xl: '24px', xxl: '34px', xxxl: '40px' },
                  }}
                >
                  <GenericHeading
                    textAlign='center'
                    sx={{
                      fontSize: { md: '13px', lg: '14px', xl: '16px', xxl: '26px', xxxl: '38px' },
                      color: '#103072',
                      fontWeight: 600,
                    }}
                  >
                    Total
                  </GenericHeading>
                  <GenericPieChart chartData={totalGraphData} />
                </Stack>
              ) : (
                NOT_AVAILABLE
              )}
              {totalGraphData ? <ChartLegend legendNames={totalGraphData.map(datum => datum.name)} /> : NOT_AVAILABLE}
            </Stack>
          </Stack>
        )}
      </ChartWrapper>

      <ChartWrapper>
        {isLoadingBar ? (
          <VerticalBarChartLoader />
        ) : (
          <Stack width='100%' height='100%'>
            {isLoadingBar ? (
              <VerticalTabsHeadingLoader />
            ) : (
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                width='100%'
                sx={{ pb: { xxl: '22px' } }}
              >
                <GenericHeading
                  sx={{
                    fontSize: { md: '14px', lg: '15px', xl: '18px', xxl: '28px', xxxl: '36px' },
                    color: '#1C2842',
                    fontWeight: 600,
                  }}
                >
                  TOTAL REVENUE (
                  {totalRevenueGraphData?.reduce((acc: number, item: any) => acc + Number(item.total), 0)
                    ? formatAmount(
                        totalRevenueGraphData.reduce((acc: number, item: any) => acc + Number(item.total), 0),
                        currentCurrency,
                      )
                    : formatAmount(0, currentCurrency)}
                  )
                </GenericHeading>
                <DatePicker
                  popperPlacement={periodPopperPlacement}
                  periodStart={barPeriod.start}
                  periodEnd={barPeriod.end}
                  handlePeriodChange={handleBarsPeriodChange}
                  format={periodFormat}
                  isCharts
                  iconUrl={calendarIconUrl}
                />
              </Stack>
            )}
            <Stack sx={{ flexGrow: 1 }}>
              <Stack
                direction='row'
                width='100%'
                alignItems='center'
                gap='4px'
                sx={{
                  borderBlockEnd: '1px solid #DFDFDF',
                }}
              >
                <Stack width='25%'>
                  <ChartLegend
                    legendNames={
                      totalRevenueGraphData?.every(item => !item.estimated)
                        ? ['Reported', 'Documented']
                        : ['Reported', 'Documented', 'Estimated']
                    }
                  />
                </Stack>
                <Stack width='75%' height={{ md: 130, xl: 180, xxl: 250, xxxl: 600 }}>
                  {totalRevenueGraphData && (
                    <GenericBarsChart
                      chartData={totalRevenueGraphData}
                      xAxisDataKey='date'
                      barDataKey1='reported'
                      stackedBarDataKey1='documented'
                      barDataKey2={'estimated'}
                      labels={{ exact: 'Estimated' }}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack
                sx={{
                  overflowX: 'auto',
                  maxWidth: '100%',
                  width: '100%',
                  marginTop: 'auto',
                  ...theme.mixins.customScrollBar(),
                }}
              >
                {totalRevenueDataForFinancialOverview && (
                  <FinancialOverview
                    financialData={totalRevenueDataForFinancialOverview}
                    labels={labelsForFinancialOverview}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </ChartWrapper>
    </VerticalOverview>
  )
}

export default Overview
